import {Component, EventEmitter, Input, OnInit, Output, Renderer2} from '@angular/core';
import {Router} from "@angular/router";
import {SocialAuthService, SocialUser} from "@abacritt/angularx-social-login";
import {BackendApiService} from "../../../services/backend-api.service";
import {AlertService} from "../../../services/alerts.service";
import {ThemeModeService} from "../../../_metronic/partials/layout/theme-mode-switcher/theme-mode.service";
import {getUserInitials} from "../../../utils/helpers";
import {isLocal, isTesting, isStaging} from "../../../utils/app_env";

@Component({
  selector: 'app-auth-login',
  templateUrl: './auth-login.component.html',
  styleUrls: ['./auth-login.component.css'],
})
export class AuthLoginComponent implements OnInit {

  protected readonly getUserInitials = getUserInitials;
  protected readonly isLocal = isLocal;
  protected readonly isTesting = isTesting;
  protected readonly isStaging = isStaging;
  hostPrefix: string;
  currentEntityHost: string;

  entities = [
    {
      name: 'Game Lounge',
      host: 'gamelounge.com',
      logo: '/assets/media/logos/entities/gamelounge.com/gl_logo_primary.svg',
    }, {
      name: 'Adventrum',
      host: 'adventrum.com',
      logo: '/assets/media/logos/entities/adventrum.com/adventrum_a.svg',
    }, 
    {
      name: 'Kiickr',
      host: 'kiickr.com',
      logo: '/assets/media/logos/entities/kiickr.com/kiickr_no_BG.svg',
    },
    {
      name: 'TodaysWeb',
      host: 'todaysweb.com',
      logo: '/assets/media/logos/entities/todaysweb.com/todaysweb_white.svg',
    }
  ]

  @Input() showEntities: boolean = true;
  @Input() showThemeSwitcher: boolean = true;
  @Input() message: string | null = null;
  @Input() redirectOnLogin: boolean = true;
  @Input() compact: boolean = false;
  @Output() onLoggedIn = new EventEmitter();
  loggedInUser: SocialUser | null = null;
  loggedInUserIsAdmin: boolean = false;
  profilePicUrl: string | null = null;
  profilePicLoaded: boolean = false;
  // From sign-in until the photo is known and loaded (or known to be missing) — shows the loading circle
  profilePicPending = false;
  isMobile: boolean = false;
  // The page the user goes back to after login (shown under "Signing in...")
  nextPage: {label: string, path: string} | null = null;

  constructor(
    private alertService: AlertService,
    private socialAuthService: SocialAuthService,
    private backendApiService: BackendApiService,
    private modeService: ThemeModeService,
    private renderer: Renderer2,
    private router: Router,
  ) {}

  ngOnInit() {
    this.hostPrefix = location.host.split('.')[0];
    this.currentEntityHost = location.host.replace(this.hostPrefix + '.', '');
    // Same breakpoint the login card itself switches at (w-md-650px, 768px) — the Google
    // button's own personalized "Sign in as ..." rendering can be quite wide, so it's shrunk
    // to fit a narrow phone screen rather than overflowing/dominating it.
    this.isMobile = window.innerWidth < 768;
    if (this.redirectOnLogin) {
      this.nextPage = this.describeNextPage(this.safeNextUrl(sessionStorage.getItem("next")));
    }

    this.socialAuthService
      .authState
      .subscribe((user) => {
        if (user) {
          this.logIntoHexagon(user);
        }
      });

    if (!this.compact) {
      if (this.getThemeMode() === 'light') {
        this.changeBodyBackgroundImage('./assets/media/auth/bg10.jpeg');
      } else if (this.getThemeMode() === 'dark') {
        this.changeBodyBackgroundImage('./assets/media/auth/bg10-dark.jpeg');
      }
    }

    if (localStorage.getItem('autologout') == '1') {
      localStorage.removeItem('autologout');
      this.alertService.alertWarning('Please, log in again');
    }
  }

  logIntoHexagon(socialUser: SocialUser) {
    const thisComponent = this;
    this.loggedInUser = socialUser;
    this.profilePicLoaded = false;
    this.profilePicPending = true;

    this.backendApiService
      .post('/auth/login/social', socialUser)
      .subscribe({
        next(res: any) {
          const next = thisComponent.safeNextUrl(sessionStorage.getItem("next"));

          const redirect = () => {
            if (!thisComponent.redirectOnLogin) {
              thisComponent.onLoggedIn.emit();
              return;
            }
            if (next) {
              sessionStorage.removeItem("next");
              location.href = next;
              return;
            }
            location.href = '/new_cdb/dashboard';
          };

          thisComponent.loggedInUserIsAdmin = !!res["sessionData"]?.["is_admin"];
          const picUrl = res["sessionData"]?.["pic-internal"];
          const isValidPic = picUrl && !picUrl.endsWith("null");

          if (isValidPic) {
            thisComponent.profilePicLoaded = false;
            const timeoutId = setTimeout(redirect, 1500);
            const preload = new Image();
            preload.onload = () => {
              clearTimeout(timeoutId);
              thisComponent.profilePicUrl = picUrl;
              thisComponent.profilePicLoaded = true;
              thisComponent.profilePicPending = false;
              setTimeout(redirect, 400);
            };
            preload.onerror = () => {
              clearTimeout(timeoutId);
              thisComponent.profilePicPending = false;
              redirect();
            };
            preload.src = picUrl;
          } else {
            thisComponent.profilePicPending = false;
            redirect();
          }
        },
        error(e) {
          console.log(e);
          thisComponent.loggedInUser = null;
          thisComponent.loggedInUserIsAdmin = false;
          thisComponent.profilePicUrl = null;
          thisComponent.profilePicPending = false;
          thisComponent.alertService.alertError('Your account is not allowed!')
        }
      });
  }

  // The page to return to after login ('next' can come from a ?next= link), only on this site: anything else
  // (https://evil.com, //evil.com, javascript:) would send a freshly logged-in user elsewhere
  private safeNextUrl(next: string | null): string | null {
    if (!next) return null;
    try {
      const url = new URL(next, location.origin);
      return url.origin === location.origin ? url.pathname + url.search + url.hash : null;
    } catch (e) {
      return null;
    }
  }

  // '/new_cdb/operator_logins/11529/deals?x=1' -> {label: 'Accounts › Deals', path: '/operator_logins/11529/deals'}; the first
  // part is named by its route title when it has one. Null for no page or the dashboard (where login goes anyway).
  private describeNextPage(next: string | null): {label: string, path: string} | null {
    const path = next?.split(/[?#]/)[0].replace(/^\/new_cdb/, '').replace(/\/+$/, '') || '';
    if (!path || path === '/dashboard') return null;
    const format = (part: string) => part.replace(/[_-]+/g, ' ').replace(/^./, char => char.toUpperCase());
    const parts = path.split('/').filter(part => part && !/^\d+$/.test(part));
    if (!parts.length) return null;
    const title = this.router.config.find(route => route.path === parts[0])?.title;
    const label = [typeof title === 'string' ? title : format(parts[0]), ...parts.slice(1).map(format)].join(' › ');
    return {label, path};
  }

  getThemeMode() {
    return this.modeService.mode.value;
  }

  switchThemeMode() {
    const newMode = this.modeService.mode.value === 'light' ? 'dark' : 'light';
    this.modeService.updateMode(newMode);
    this.changeBodyBackgroundImage(
      newMode === 'light' ? './assets/media/auth/bg10.jpeg' : './assets/media/auth/bg10-dark.jpeg'
    );
  }

  changeBodyBackgroundImage(imageUrl: string) {
    this.renderer.setStyle(document.body, 'background-image', `url(${imageUrl})`);
  }

}